import { Link, useLocation } from 'react-router-dom';
import { useLanguageContext } from '@/i18n/LanguageContext';

export type Crumb = { label: string; labelEn?: string; to?: string };

const SITE_ORIGIN = 'https://butler-academy.jp';

/**
 * 共通パンくず。呼び出し側は2階層目以降だけを渡す(「ホーム」は自動付与)。
 * to を持たない要素は現在地としてリンクにしない。
 * BreadcrumbList の JSON-LD も同時に出力する。
 */
export default function Breadcrumb({ items }: { items: Crumb[] }) {
  const { lang } = useLanguageContext();
  const location = useLocation();
  const isEn = lang === 'en';
  const prefix = isEn ? '/en' : '';

  const withLang = (path: string) => {
    if (path === '/') return prefix === '' ? '/' : prefix;
    return `${prefix}${path}`;
  };

  const pick = (c: Crumb) => (isEn && c.labelEn ? c.labelEn : c.label);
  const crumbs: Crumb[] = [{ label: isEn ? 'Home' : 'ホーム', to: '/' }, ...items];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: pick(crumb),
      item: SITE_ORIGIN + (crumb.to !== undefined ? withLang(crumb.to) : location.pathname),
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <nav aria-label="パンくずリスト" className="bg-background-50 border-b border-background-200">
        <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16 py-3 flex items-center gap-2 text-[13px] text-foreground-500">
          {crumbs.map((crumb, index) => {
            const isLast = index === crumbs.length - 1;
            return (
              <span
                key={`${pick(crumb)}-${index}`}
                className={`flex items-center gap-2 ${isLast ? 'min-w-0' : 'shrink-0'}`}
              >
                {index > 0 && (
                  <i className="ri-arrow-right-s-line text-foreground-400 text-[12px] shrink-0"></i>
                )}
                {isLast || crumb.to === undefined ? (
                  <span className="text-foreground-700 truncate">{pick(crumb)}</span>
                ) : (
                  <Link to={withLang(crumb.to)} className="hover:text-accent-600 transition-colors">
                    {pick(crumb)}
                  </Link>
                )}
              </span>
            );
          })}
        </div>
      </nav>
    </>
  );
}
